import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

const CarouselContext = React.createContext({});

/* ========================================================================= */
/* Providers(s) */
/* ========================================================================= */

const CarouselProvider = (props) => {
    // state
    const [swiper, setSwiper] = useState(null);
    const [isPrevDisabled, setIsPrevDisabled] = useState(false);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    // render
    return (
        <CarouselContext.Provider
            value={{
                swiper,
                setSwiper,
                isPrevDisabled,
                setIsPrevDisabled,
                isNextDisabled,
                setIsNextDisabled,
                currentIndex,
                setCurrentIndex
            }}
            {...props}>
            {props.children}
        </CarouselContext.Provider>
    );
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof carouselPropTypes>} CarouselProps
 * @param {Omit<CarouselProps, 'swiperConfigProps'> & ExtendedProps}  props
 */
const Carousel = ({
    items = [],
    perItemTypeClasses = false,
    slideClassName = null,
    swiperConfig = {}
}) => {
    const { setSwiper, setIsPrevDisabled, setIsNextDisabled, setCurrentIndex } = useContext(CarouselContext);

    /** @param {import('swiper').Swiper} _swiper */
    const refreshSwiperStates = (_swiper) => {
        setCurrentIndex(_swiper.realIndex);
        setIsPrevDisabled(_swiper.isBeginning);
        setIsNextDisabled(_swiper.isEnd);
    };

    /** @type {Swiper} */
    const config = {
        onRealIndexChange: refreshSwiperStates,
        onSwiper: (_swiper) => {
            setSwiper(_swiper);
            refreshSwiperStates(_swiper);
        },
        slidesPerView: 1,
        watchSlidesVisibility: true,
        wrapperTag: 'ul',
        ...swiperConfig
    };

    return (
        <Swiper
            className='swiper-wrapper'
            {...config}>
            {items.map((item, i) => {
                let perItemTypeClass = '';
                if (perItemTypeClasses) {
                    slideClassName = null;

                    switch (item?.props?.item?.__typename) {
                    case 'Insight':
                        perItemTypeClass = 'col-11 sm-col-4 px-gutter';
                        break;

                    case 'People':
                        perItemTypeClass = 'col-11 sm-col-3 px-gutter';
                        break;

                    default:
                        perItemTypeClass = 'col-11 sm-col-5 px-gutter';
                        break;
                    }
                }

                return (
                    <SwiperSlide
                        className={`swiper-slide${slideClassName ? ' ' + slideClassName : ''}${perItemTypeClass ? ' ' + perItemTypeClass : ''}`}
                        index={i}
                        key={i}
                        tag='li'>
                        {item}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Carousel.propTypes = {
    items: PropTypes.array,
    perItemTypeClasses: PropTypes.bool,
    slideClassName: PropTypes.string,
    swiperConfig: PropTypes.object
};

CarouselProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element,
        PropTypes.string,
    ])
};

export { Carousel, CarouselProvider, CarouselContext };

export default Carousel;
